import { useEffect, useRef } from "react";
import { getJobStatus, getJobFileCount, type BatchJob } from "@/utils/api/batchProcessing";
import { useToast } from "@/hooks/useToast";
import { useBatchJobs } from "@/store";

/**
 * Watches batch jobs from the store and shows a toast when a job completes.
 * Tracks previously-seen terminal jobs to avoid showing toasts for old completions.
 *
 * Requires the batch jobs subscription to be started via startBatchJobsSubscription()
 * (done in RapidDeployModal).
 */
export function useBatchJobNotifications(onViewResults?: () => void) {
    const toast = useToast();
    const jobs = useBatchJobs();
    const seenTerminalJobIds = useRef<Set<string>>(new Set());
    const isFirstUpdate = useRef(true);

    useEffect(() => {
        if (jobs.length === 0) return;

        if (isFirstUpdate.current) {
            // On first update, record all already-terminal jobs so we don't toast them
            jobs.forEach((job: BatchJob) => {
                if (getJobStatus(job) === "done" || getJobStatus(job) === "error") {
                    seenTerminalJobIds.current.add(job.id);
                }
            });
            isFirstUpdate.current = false;
            return;
        }

        // Check for newly completed jobs
        jobs.forEach((job: BatchJob) => {
            const status = getJobStatus(job);
            if (status === "done" && !seenTerminalJobIds.current.has(job.id)) {
                seenTerminalJobIds.current.add(job.id);
                const fileCount = getJobFileCount(job);
                const fileLabel = fileCount ? `${fileCount} files` : "your files";

                toast.custom({
                    level: "purbo",
                    children: `Your model is done running on ${fileLabel}!`,
                    actions: onViewResults
                        ? [{ label: "Download Results", onClick: onViewResults }]
                        : [],
                    toastId: `batch-complete-${job.id}`,
                    autoClose: false
                });
            }

            // Also track error jobs so we don't re-notify
            if (status === "error" && !seenTerminalJobIds.current.has(job.id)) {
                seenTerminalJobIds.current.add(job.id);
            }
        });
    }, [jobs, toast, onViewResults]);
}
